import fields from './fields';
import { fetchSheetFormulas, makeFormulaBuilder } from './exportFormulas';

//Shared Excel export for the data sheets — used by FloatingDownloadButton
//(desktop edge button) and the mobile sheet toolbar (MOBILE_PLAN.md 2.2).
//Exports the visible columns of fieldOrder, with a totals row for money columns.
//
//exceljs rather than xlsx, and dynamic-imported at call time for the same reason
//import/template.js does it: it's a heavy dependency only needed when someone
//actually exports. It is heavier than xlsx — 264KB gz against 139KB gz — so the
//first export on a session costs about 125KB more than it used to. That was
//accepted knowingly in exchange for styling and first-class date/formula cells.
//
//Note the repo still writes xlsx elsewhere (import/errorsWorkbook.js,
//PaymentInsights.jsx) and reads with it (import/steps/UploadStep.jsx), so this
//is not a clean library split — just the right tool for this file.
//
//The workbook is LIVE, not a picture of one: dates are real date cells and the
//totals row is =SUM(), so deleting a row in Excel moves the total and
//subtracting two dates gives a number instead of #VALUE!.
//
//This file was on SheetJS until it was found that SheetJS's community build
//silently drops cell styling on write — the bold white-on-blue header this code
//claimed to apply never reached a single user's file. exceljs writes styles,
//dates and formula cache values as first-class citizens. Don't move it back.

//Which sheets hand the export one page rather than the whole set. The three
//absent ones — Inventory, Committed, Credit — are served by routes that return
//every row for the dealership (routes/inventory.js, routes/sales.js:16-40), so
//their export IS the full set and must not claim otherwise.
export const PAGINATED_SHEETS = new Set([
    'Cash', 'CreditCleared', 'Cancelled', 'Customers', 'Brokers', 'Sellers',
]);

const HEADER_FILL = 'FF4472C4';
const TOTALS_FILL = 'FFE7E6E6';

export const defaultExportName = (sheetName) =>
    `${sheetName}_${new Date().toISOString().split('T')[0]}`;

/**
 * @param scope 'page' | 'all' — what the rows represent, which decides the
 *        totals-row label. Omitted, it is inferred from whether the sheet
 *        paginates, which is right for every caller that hasn't opted into the
 *        export-all dialog.
 */
export default async function exportSheetToExcel(data, fieldOrder, sheetName, {
    filename = defaultExportName(sheetName),
    scope = null,
    redactedFields = null,   //hiddenCostFields — gates the derived formulas
    includeHidden = false,   //also export columns the user has hidden on screen
} = {}) {
    if (!data || data.length === 0) {
        alert('No data to download');
        return;
    }

    //Fetched in parallel with the library: a failed probe returns {} and the
    //export falls back to values, exactly as it behaved before formulas existed.
    const [ExcelJSmod, formulas] = await Promise.all([
        import('exceljs'),
        fetchSheetFormulas(sheetName),
    ]);
    const ExcelJS = ExcelJSmod.default || ExcelJSmod;   //same shim as import/template.js
    const visibleFields = getVisibleFields(fieldOrder, includeHidden);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(sheetName);
    buildSheet(ws, data, visibleFields, sheetName, scope, formulas, redactedFields);

    const buffer = await wb.xlsx.writeBuffer();
    triggerDownload(buffer, `${filename}.xlsx`);
}

//Same mechanism as the import template's download (import/steps/UploadStep.jsx).
function triggerDownload(arrayBuffer, filename) {
    const blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

//Which columns the workbook carries.
//
//`public` is never relaxed: those are internal keys (DealershipID, VehicleID,
//PrimaryImage) and the raw cost inputs, and they are not shown on any sheet.
//
//`hidden` is the user's own on-screen choice, so includeHidden re-admits
//exactly the columns they could unhide themselves through the Columns dialog —
//it discloses nothing new. Cost fields gated by the dealership are already
//absent from fieldOrder before it reaches here (useFieldOrder filters
//hiddenCostFields on the way out), so this cannot resurrect one.
//
//It also has a second effect worth knowing: derived columns only become
//formulas when all of their inputs are present, and several inputs (Duty, Days,
//DailyDemurrage, CommitmentDate, MonthsSinceDelivery) are hidden by default.
//Including them turns those formulas on.
const getVisibleFields = (fieldOrder, includeHidden = false) =>
    fieldOrder.filter(f => (includeHidden || !f.hidden) && fields[f.name]?.public);

//How many extra columns includeHidden would add — for the dialog's label.
export const hiddenColumnCount = (fieldOrder) =>
    fieldOrder.filter(f => f.hidden && fields[f.name]?.public).length;

const isNumeric = (fieldDef) =>
    fieldDef?.dataType === 'decimal' ||
    fieldDef?.dataType === 'integer' ||
    fieldDef?.dataType === 'number';

const isDate = (fieldDef) =>
    fieldDef?.dataType === 'date' || fieldDef?.dataType === 'datetime';

const numberFormat = (fieldDef) =>
    fieldDef.dataType === 'decimal' ? '#,##0.00' : '#,##0';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

//A 'date' field reaching here is already the bare "YYYY-MM-DD" string that
//normalizeRecords produced at ingress (UtilityFunctions.jsx formatFromInput does
//value.split("T")[0]), and that same string is what the sheet displays.
//
//Read the year/month/day straight out of it and rebuild the date in UTC. Do NOT
//route it through new Date("2026-07-13"): that parses as UTC midnight, and any
//later reading of local components shifts the day for anyone at a negative UTC
//offset. exceljs converts a Date to a serial via UTC — verified identical across
//UTC, +3, -5 and +14 — so a UTC-built date lands on the right day everywhere.
const toDate = (value) => {
    const m = typeof value === 'string' && value.match(DATE_ONLY);
    if (!m) return null;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return isNaN(d.getTime()) ? null : d;
};

//'datetime' keeps a full ISO timestamp, which denotes an instant rather than a
//calendar day, so it converts directly.
const toDateTime = (value) => {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const cellValue = (row, f) => {
    const value = row[f.name];
    if (value === null || value === undefined || value === '') return null;

    const fieldDef = fields[f.name];
    if (isDate(fieldDef)) {
        const d = fieldDef.dataType === 'datetime' ? toDateTime(value) : toDate(value);
        //An unparseable date falls back to the raw value rather than a blank, so
        //a malformed row shows something wrong instead of silently losing data.
        return d ?? value;
    }
    if (isNumeric(fieldDef)) {
        //DECIMAL columns come back from mysql2 as strings to keep precision
        const n = typeof value === 'number' ? value : parseFloat(value);
        return isNaN(n) ? null : n;
    }
    return value;
};

//What the totals row actually covers. An explicit scope wins; otherwise it is
//inferred, and a paginated sheet is assumed to have handed us one page.
const totalsLabel = (sheetName, rowCount, scope) => {
    const isPage = scope ? scope === 'page' : PAGINATED_SHEETS.has(sheetName);
    return isPage
        ? 'TOTAL (this page)'
        : `TOTAL (all ${rowCount} record${rowCount === 1 ? '' : 's'})`;
};

//Columns whose SQL reads CURDATE() — their values are true only at the moment of
//export. They stay values rather than =TODAY() formulas on purpose: a saved
//sheet that silently reports different overdue days and penalties every time it
//is reopened is a document that lies. The stamp below makes the freeze explicit.
const AS_OF_FIELDS = ['DaysOverdue', 'TotalPenalty', 'Demurrage'];

function buildSheet(ws, data, visibleFields, sheetName, scope, formulas, redactedFields) {
    ws.addRow(visibleFields.map(f => fields[f.name]?.displayName || f.name));
    data.forEach(row => ws.addRow(visibleFields.map(f => cellValue(row, f))));

    const totalsRowNo = data.length + 2;        //1-based: header + rows + 1
    const firstDataRow = 2;
    const lastDataRow = data.length + 1;

    //Where each field landed, so the server's symbolic expressions can become
    //cell references. Only columns actually in this export appear, which is
    //what lets the builder refuse a formula whose inputs are hidden or gated.
    const columnOf = new Map(visibleFields.map((f, i) => [f.name, columnLetter(i + 1)]));
    const formulaFor = makeFormulaBuilder(formulas, columnOf, redactedFields);

    //Derived columns become live formulas carrying the server's own value as
    //the cached result. Excel shows that on open and recalculates the moment
    //anything is edited — so a disagreement between the two IS drift between
    //this transcription and the SQL.
    visibleFields.forEach((f, i) => {
        for (let r = firstDataRow; r <= lastDataRow; r++) {
            const expr = formulaFor(f.name, r);
            if (!expr) continue;
            const cell = ws.getCell(r, i + 1);
            const cached = cell.value;
            //Never replace a blank with a formula: an empty row would turn a
            //legitimately empty cell into a 0 or an error.
            if (cached === null || cached === undefined) continue;
            cell.value = { formula: expr, result: cached };
        }
    });

    const totalsRow = ws.getRow(totalsRowNo);
    totalsRow.getCell(1).value = totalsLabel(sheetName, data.length, scope);

    visibleFields.forEach((f, i) => {
        const col = i + 1;                       //exceljs columns are 1-based
        const fieldDef = fields[f.name];
        if (!fieldDef) return;

        const dateCol = isDate(fieldDef);
        if (dateCol || isNumeric(fieldDef)) {
            const fmt = dateCol
                ? (fieldDef.dataType === 'datetime' ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy')
                : numberFormat(fieldDef);
            for (let r = firstDataRow; r <= lastDataRow; r++) {
                ws.getCell(r, col).numFmt = fmt;
            }
        }

        //Only columns flagged total:true in fields.js get a sum — see the note
        //there on why Model Year and friends must not.
        if (!fieldDef.total) return;

        //The cached `result` matters. Measured with LibreOffice: given a formula
        //and no cached value it computes the right answer, but given one it
        //displays that and does NOT recalculate on load. So the cache is what a
        //user sees on open and must itself be correct — it is summed from the
        //same rows written above. The formula takes over the moment anything is
        //edited, which is the point: delete a row and the total follows. It also
        //keeps non-calculating readers (scripts, pandas) seeing a number.
        const sum = data.reduce((acc, row) => {
            const n = typeof row[f.name] === 'number' ? row[f.name] : parseFloat(row[f.name]);
            return isNaN(n) ? acc : acc + n;
        }, 0);

        const letter = columnLetter(col);
        const cell = totalsRow.getCell(col);
        cell.value = { formula: `SUM(${letter}${firstDataRow}:${letter}${lastDataRow})`, result: sum };
        cell.numFmt = numberFormat(fieldDef);
    });

    styleSheet(ws, visibleFields, totalsRowNo);
    stampAsOf(ws, visibleFields, totalsRowNo);
}

//A1-style column letter for a 1-based index (1 -> A, 27 -> AA).
function columnLetter(n) {
    let s = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        s = String.fromCharCode(65 + rem) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

function styleSheet(ws, visibleFields, totalsRowNo) {
    for (let c = 1; c <= visibleFields.length; c++) ws.getColumn(c).width = 15;

    const header = ws.getRow(1);
    header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    header.alignment = { horizontal: 'center', vertical: 'middle' };
    for (let c = 1; c <= visibleFields.length; c++) {
        header.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    }
    ws.views = [{ state: 'frozen', ySplit: 1 }];

    const totals = ws.getRow(totalsRowNo);
    totals.font = { bold: true };
    for (let c = 1; c <= visibleFields.length; c++) {
        totals.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTALS_FILL } };
    }
}

//Date the as-at columns, two rows below the totals so it can't be mistaken for
//data and won't be swept into a =SUM range.
function stampAsOf(ws, visibleFields, totalsRowNo) {
    const present = AS_OF_FIELDS
        .filter(name => visibleFields.some(f => f.name === name))
        .map(name => fields[name]?.displayName || name);
    if (!present.length) return;

    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const cell = ws.getRow(totalsRowNo + 2).getCell(1);
    cell.value = `${present.join(', ')} calculated as of ${dd}/${mm}/${today.getFullYear()} — these do not update when this file is reopened.`;
    cell.font = { italic: true, size: 9 };
}
